/**
 * @file
 * Site-wide mega menu behavior (we_megamenu contrib block, header).
 *
 * Ported from the enable() function in
 * source/default/_patterns/02-molecules/menu/index.js, from jQuery to
 * vanilla JS. It does two things:
 *
 * 1. Activates the mega menu. The theme's override of the contrib JS
 *    (apps/drupal/js/we_megamenu_frontend.js) only calls init() and
 *    mobileMenu() on `window.load`. PL called both again from its design
 *    system behavior ("Chrome issue"), so the menu works even when `load`
 *    has already fired before that listener is bound. Kept here unchanged.
 * 2. On desktop "click-action" menus, adds `extra-padding` to an open
 *    submenu that is taller than the viewport, and re-checks on resize
 *    (debounced), so its last links can scroll clear of the fixed header.
 *
 * Deliberately NOT named menu.js: Drupal core auto-attaches
 * `[machine_name].js` from the component directory whenever the component
 * renders, on top of the webpack sdc.js copy. The mega menu is not rendered
 * by this component's own Twig, so this file is only bundled through
 * components/index.js. Same approach as svg/fontawesome.js (MCFSFT-4958).
 *
 * Differences from PL, none of them visible:
 * - Binds once per element (data-attribute guard). PL re-ran on every
 *   behavior attach, including AJAX responses, stacking duplicate handlers.
 * - A clicked link whose <li> has no child submenu is skipped. PL threw a
 *   TypeError in that case (reading clientHeight of undefined).
 */
((Drupal) => {
  const MEGAMENU = 'nav.navbar-we-mega-menu';
  const TOGGLE = 'div.region-we-mega-menu a.navbar-toggle';
  const LINKS = 'ul li.dropdown-menu > a';

  // Only one resize handler at a time, for the most recently clicked submenu
  // (PL: $(window).off('resize.megamenuPadding').on(...)).
  let resizeHandler = null;

  // @see https://www.freecodecamp.org/news/javascript-debounce-example/
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function setResizeHandler(handler) {
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler);
    }
    resizeHandler = handler;
    if (handler) {
      window.addEventListener('resize', handler);
    }
  }

  function onLinkClick(event) {
    const li = event.currentTarget.closest('li');
    const submenu = li
      ? Array.from(li.children).find((child) =>
          child.classList.contains('we-mega-menu-submenu')
        )
      : null;
    if (!submenu) {
      return;
    }
    const adjustPadding = () => {
      const submenuHeight = submenu.clientHeight + submenu.offsetTop;
      // Same measurement as jQuery 3's $(window).height().
      submenu.classList.toggle(
        'extra-padding',
        submenuHeight > document.documentElement.clientHeight
      );
    };
    adjustPadding();
    setResizeHandler(debounce(adjustPadding, 200));
  }

  // eslint-disable-next-line no-param-reassign
  Drupal.behaviors.menuMegaMenu = {
    attach(context) {
      const frontEnd = Drupal.WeMegaMenuFrontEnd;

      // 1. Activate the mega menu. init() guards itself; mobileMenu() binds
      // against the whole page, so run it once per toggle element.
      const toggles = context.querySelectorAll
        ? context.querySelectorAll(TOGGLE)
        : [];
      if (frontEnd && typeof frontEnd.init === 'function') {
        frontEnd.init();
      }
      toggles.forEach((toggle) => {
        if (toggle.dataset.menuMegaMenuInitialized) {
          return;
        }
        // eslint-disable-next-line no-param-reassign
        toggle.dataset.menuMegaMenuInitialized = 'true';
        if (frontEnd && typeof frontEnd.mobileMenu === 'function') {
          frontEnd.mobileMenu();
        }
      });

      // 2. Extra padding for tall submenus. Bound after init() so this runs
      // after the contrib click handler has toggled `clicked` on the item,
      // i.e. it measures the submenu in its open state, as PL did.
      const menus = context.querySelectorAll
        ? context.querySelectorAll(`${MEGAMENU}.click-action`)
        : [];
      menus.forEach((megamenu) => {
        megamenu.querySelectorAll(LINKS).forEach((link) => {
          if (link.dataset.menuMegaMenuInitialized) {
            return;
          }
          // eslint-disable-next-line no-param-reassign
          link.dataset.menuMegaMenuInitialized = 'true';
          link.addEventListener('click', onLinkClick);
        });
      });
    },

    detach(context, settings, trigger) {
      if (trigger !== 'unload' || !context.querySelectorAll) {
        return;
      }
      context.querySelectorAll(TOGGLE).forEach((toggle) => {
        // eslint-disable-next-line no-param-reassign
        delete toggle.dataset.menuMegaMenuInitialized;
      });
      const links = context.querySelectorAll(`${MEGAMENU} ${LINKS}`);
      links.forEach((link) => {
        link.removeEventListener('click', onLinkClick);
        // eslint-disable-next-line no-param-reassign
        delete link.dataset.menuMegaMenuInitialized;
      });
      if (links.length) {
        setResizeHandler(null);
      }
    },
  };
})(Drupal);
